import { useCallback } from "react";

import {
  buildInAppHref,
  resolveEntityRoute,
} from "../helpers/ai-advisor.helpers";
import { usePrefixedRouting } from "./use-prefixed-routing";

/**
 * Absolute href for an assistant `navigate` / `entityRef` target, or `undefined` when the
 * entity has no screen and the caller should render plain text instead.
 *
 * The company prefix is not cosmetic, and leaving it out fails in a way that still looks
 * wired up. Only the `/c/:companyId/{entity}/*` route mounts the actual page; the
 * unprefixed variant mounts `RoutingUnit` with no child, and `CompanyRedirectWrapper` then
 * sends the user to `/c/{companyId}/{basePath}` — dropping the trailing `/edit/{id}`. A
 * chip built without the prefix therefore opens the LIST rather than the record.
 *
 * Every other in-app link in the product (grid id columns, sidebar, settings) goes through
 * `usePrefixedRouting`; this is the assistant's equivalent, kept in one place so a second
 * call site cannot forget it.
 */
export const useEntityHref = (): ((
  entity: string,
  id?: number,
) => string | undefined) => {
  const { addPrefix } = usePrefixedRouting();

  return useCallback(
    (entity: string, id?: number): string | undefined => {
      const route = resolveEntityRoute(entity, id);

      return route ? buildInAppHref(addPrefix(route)) : undefined;
    },
    [addPrefix],
  );
};
